import React, { useEffect } from 'react';
import { Box } from 'src/components/ui/box';
import { useAudioVisualizer } from 'src/hooks/speechToText/useAudioVisualizer';
import { tick } from 'src/redux/slices/smartMeet';
import { useDispatch, useSelector } from 'src/redux/store';
import audioRecorderService from 'src/utils/audioRecorder';
import { cn } from 'src/utils/utils';

interface RecorderProps {
  isRecording: boolean;
  mode?: 'minimized' | 'maximized';
}

const Recorder: React.FC<RecorderProps> = ({
  isRecording,
  mode = 'maximized'
}) => {
  const { seconds } = useSelector((state) => state.smartMeet);
  const dispatch = useDispatch();
  const { canvasRef, setupAudioGraph, stopVisualizer } = useAudioVisualizer();

  useEffect(() => {
    if (!isRecording) {
      stopVisualizer();
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const attachVisualizer = () => {
      if (cancelled) {
        return;
      }
      const stream = audioRecorderService.getStream();
      if (stream) {
        setupAudioGraph(stream);
      } else {
        retryTimer = setTimeout(attachVisualizer, 100);
      }
    };

    attachVisualizer();

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      stopVisualizer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(tick());
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const formattedTime = new Date(seconds * 1000).toISOString().substr(11, 8);

  const isMinimized = mode === 'minimized';
  const canvasHeight = isMinimized ? 18 : 30;
  const timeFontClass = isMinimized
    ? 'tw:text-[12px] tw:tabular-nums'
    : 'tw:text-[10px] tw:sm:text-[14px]';

  return (
    <Box
      className={cn(
        ` tw:flex tw:items-center tw:gap-s tw:w-full tw:max-w-full tw:overflow-hidden`,
        !isMinimized ? 'tw:pr-4' : ''
      )}
      style={{ height: canvasHeight }}
    >
      <Box className="tw:flex-1 tw:min-w-0 tw:h-full">
        <canvas
          ref={canvasRef}
          height={canvasHeight}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 8,
            display: 'block'
          }}
        />
      </Box>

      <Box
        className={cn(
          'tw:shrink-0 tw:w-fit tw:text-center tw:rounded-[8px] tw:whitespace-nowrap tw:leading-none tw:self-center',
          timeFontClass
        )}
      >
        {formattedTime}
      </Box>
    </Box>
  );
};

export default Recorder;
